import { GNS } from '../core/GNS.js';

/**
 * GNS.CasesScene
 * * 実績一覧ページ（/cases/）のメイン制御クラス
 * * 機能:
 * - 実績一覧の表示
 * - Vimeo動画の埋め込みとホバー再生制御
 * - 無限スクロール & Moreボタン
 * - 外部フィルタリングスクリプト(initCasesFilter)との連携
 */
GNS.CasesScene = function (GNS) {
    // AbstractSceneを継承
    const scene = GNS.create(GNS.AbstractScene);

    /**
     * Cases 初期化処理
     */
    scene.init = function (data) {
        scene._super.init.call(this, data);

        // ステート管理
        this.page = 1;
        this.totalPage = 1;
        this.isLoading = false;

        // スクロール自動読み込み用
        this.moreProgress = 0;
        this.timerId = null;
        this.isTimerRunning = false;

        // UI要素
        this.container = new GNS.DisplayObject("cases__container");
        this.btnMore = new GNS.DisplayObject("cases__btn_more");
        this.btnMoreShadow = new GNS.DisplayObject("cases__btn_more__shadow");

        // タッチコントローラー
        this.touchController = new GNS.TouchActionController(this.element);
        this.touchController.isPreventDefault = false;

        // イベント登録
        this.btnMore.addEvent("click", this.loadMore);

        // 初期化時にもフィルタリング処理の初期化を試みる
        // (DOM構築待ちのために少し遅延)
        setTimeout(() => this._initializeFiltering(), 300);
    };

    /**
     * Cases ロード処理
     * SSR（サーバーサイドレンダリング）済みか確認
     * ※APIロードロジックは template/cases/index.php 内の initCasesFilter に委譲
     */
    scene.load = function () {
        console.log("load cases");
        // PHP出力(.cases__container__item)とJS生成(.cases__container__line)の両方を考慮
        const existingItems = this.container.element.querySelectorAll('.cases__container__line, .cases__container__item');

        // タグ属性から総ページ数を取得
        const totalPageAttr = this.container.element.getAttribute('data-total-page');
        this.totalPage = totalPageAttr ? parseInt(totalPageAttr, 10) : 1;

        this._updateMoreButtonState();

        // ロード完了処理へ
        this._loadComplete();
    };

    /**
     * ロード完了共通処理
     */
    scene._bind._loadComplete = function () {
        this.isLoading = false;
        scene._super.load.call(this);

        // DOM更新後にフィルタリングの再初期化を試みる
        this._initializeFiltering();
    };

    /**
     * 外部フィルタリングスクリプトの初期化
     * (スクリプトのロード待ちを含むポーリング処理)
     */
    scene._bind._initializeFiltering = function () {
        let attempts = 0;
        const maxAttempts = 30; // 30回 * 100ms = 3秒待機

        const tryInit = () => {
            attempts++;
            if (typeof window.initCasesFilter === 'function') {
                setTimeout(() => {
                    window.initCasesFilter();
                }, 100);
            } else if (attempts < maxAttempts) {
                setTimeout(tryInit, 100);
            }
        };

        tryInit();
    };

    /**
     * Moreボタンの状態更新
     */
    scene._updateMoreButtonState = function () {
        console.log("update more button state");
        if (this.page < this.totalPage) {
            this.btnMore.addClass("show");
        } else {
            this.btnMore.removeClass("show");
        }
    };

    // ----------------------------------------------------------------
    // Event Handlers
    // ----------------------------------------------------------------

    /**
     * 表示ハンドラ
     */
    scene.showHandler = function () {
        console.log("show handler");
        // 表示時にもフィルタリング初期化を念のため実行
        setTimeout(() => {
            if (typeof window.initCasesFilter === 'function') {
                window.initCasesFilter();
            }
            this._initializeFiltering();
        }, 200);

        this.moreProgress = 0;

        this.touchController.addEventListener(GNS.TouchActionEvent.TOUCH_1_MOVE, this.dragging);
        GNS.DOM.addEvent(window, "scroll", this.scrollCheck);

        // ホイールイベント
        const wheelEventName = ("onwheel" in document) ? "wheel" :
            ("onmousewheel" in document) ? "mousewheel" : "DOMMouseScroll";
        this.element.addEventListener(wheelEventName, this.onMouseWheelHandler, { passive: true });
    };

    /**
     * 非表示ハンドラ
     */
    scene.hideHandler = function () {
        console.log("hide handler");
        this.touchController.removeEventListener(GNS.TouchActionEvent.TOUCH_1_MOVE, this.dragging);
        GNS.DOM.removeEvent(window, "scroll", this.scrollCheck);

        const wheelEventName = ("onwheel" in document) ? "wheel" :
            ("onmousewheel" in document) ? "mousewheel" : "DOMMouseScroll";
        this.element.removeEventListener(wheelEventName, this.onMouseWheelHandler);

        this._stopMoreTimer();
    };

    /**
     * リセットハンドラ
     */
    scene.resetHandler = function () {
        console.log("reset handler");
        // ボタンやVimeoプレイヤーの管理は不要になったため削除
    };

    /**
     * Moreボタンクリック
     */
    scene._bind.loadMore = function () {
        // 外部ハンドラ（index.php等で設定）がある場合はそちらを優先実行
        if (this.externalLoadMore && typeof this.externalLoadMore === 'function') {
            this.externalLoadMore();
            return;
        }

        // グローバルフォールバック確認
        if (window.casesLoadMoreGlobal && typeof window.casesLoadMoreGlobal === 'function') {
            window.casesLoadMoreGlobal();
            return;
        }

        if (!this.isLoading) {
            this.page++;
            this.load();
        }
    };

    // ----------------------------------------------------------------
    // Scroll & Auto Load Logic
    // ----------------------------------------------------------------

    scene._bind.scrollCheck = function () {
        if (!this.isLoading && this.page < this.totalPage) {
            const pageSize = GNS.DOM.getPageSize();
            const threshold = 100;
            const isNearBottom = (pageSize.scrollY + pageSize.displayHeight) >= (pageSize.stageHeight - threshold);

            if (isNearBottom) {
                this.moreProgress = 16;
                this._startMoreTimer();
            }
        }
    };

    scene._bind.onMouseWheelHandler = function (e) {
        if (!e) e = window.event;
        const delta = e.deltaY ? -(e.deltaY) : (e.wheelDelta ? e.wheelDelta : -(e.detail));
        if (delta < 0) this.scrollCheck();
    };

    scene._bind.dragging = function () {
        const deltaY = -(this.touchController.touch0CurrentY - this.touchController.touch0PrevY);
        if (deltaY > 20) this.scrollCheck();
    };

    scene._bind._startMoreTimer = function () {
        if (!this.isTimerRunning) {
            this.isTimerRunning = true;
            this.timerId = setInterval(this.moreTimeUpdate, 16);
        }
    };

    scene._bind._stopMoreTimer = function () {
        if (this.isTimerRunning) {
            clearInterval(this.timerId);
            this.isTimerRunning = false;
        }
    };

    scene._bind.moreTimeUpdate = function () {
        console.log("more time update");
        this.moreProgress *= 0.995;

        const progressPercent = GNS.Animate.easeOutQuart(Math.min(this.moreProgress, 15) / 15, 0, 100, 1);
        this.btnMoreShadow.element.style.height = `${progressPercent}%`;

        if (this.moreProgress < 0.5) {
            this.moreProgress = 0;
            this._stopMoreTimer();
            this.btnMoreShadow.element.style.height = "0%";
        } else if (this.moreProgress > 15) {
            this.moreProgress = 0;
            this._stopMoreTimer();
            this.btnMoreShadow.element.style.height = "0%";
            this.loadMore();
        }
    };

    return scene._self;
}(GNS);
